'use client';

import { useState, useEffect } from 'react';
import { initTruecaller, pollForSession } from '../lib/truecaller';
import { API_BASE_URL } from '../lib/config';

export default function TruecallerLogin({ onAuthenticated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestNonce, setRequestNonce] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const storedSession = localStorage.getItem('numdle_session');
    if (storedSession) {
      verifyStoredSession(storedSession);
    }
  }, []);

  const verifyStoredSession = async (sessionId) => {
    try {
      const sessionResponse = await fetch(`${API_BASE_URL}/api/auth/verify-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      });

      if (!sessionResponse.ok) {
        localStorage.removeItem('numdle_session');
        return;
      }

      const sessionData = await sessionResponse.json();

      const userResponse = await fetch(`${API_BASE_URL}/api/users/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        if (userData.exists) {
          onAuthenticated({ ...userData, isAdmin: sessionData.isAdmin }, sessionId);
        } else {
          onAuthenticated(sessionData, sessionId);
        }
      } else {
        onAuthenticated(sessionData, sessionId);
      }
    } catch (error) {
      console.error('Session verification error:', error);
      localStorage.removeItem('numdle_session');
    }
  };

  const handleTruecallerLogin = async () => {
    setLoading(true);
    setError(null);

    const nonce = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setRequestNonce(nonce);

    try {
      await fetch(`${API_BASE_URL}/api/auth/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestNonce: nonce })
      });

      initTruecaller(
        nonce,
        () => {
          const cleanup = pollForSession(
            nonce,
            async (sessionData) => {
              localStorage.setItem('numdle_session', nonce);
              
              try {
                const userResponse = await fetch(`${API_BASE_URL}/api/users/get`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ sessionId: nonce })
                });

                if (userResponse.ok) {
                  const userData = await userResponse.json();
                  if (userData.exists) {
                    onAuthenticated({ ...userData, isAdmin: sessionData.isAdmin }, nonce);
                  } else {
                    onAuthenticated(sessionData, nonce);
                  }
                } else {
                  onAuthenticated(sessionData, nonce);
                }
              } catch (error) {
                onAuthenticated(sessionData, nonce);
              }
              
              setLoading(false);
            },
            (error) => {
              setError(error);
              setLoading(false);
            }
          );
        },
        (error) => {
          setError(error);
          setLoading(false);
        }
      );
    } catch (error) {
      setError('Failed to initialize login');
      setLoading(false);
    }
  };

  if (!mounted) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center'
      }}>
        <h2 style={{ marginBottom: '1rem', fontSize: '24px', fontWeight: 'bold' }}>
          Welcome to Numdle
        </h2>
        <p style={{ marginBottom: '2rem', color: '#666' }}>
          Login with Truecaller to verify your phone number instantly
        </p>

        {!isAndroid && (
          <div style={{
            padding: '1rem',
            background: '#fef3c7',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '14px'
          }}>
            ⚠️ Truecaller login is only available on Android devices
          </div>
        )}

        <button
          onClick={handleTruecallerLogin}
          disabled={loading || !isAndroid}
          style={{
            width: '100%',
            padding: '1rem',
            background: loading || !isAndroid ? '#ccc' : '#0066FF',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading || !isAndroid ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          {loading ? (
            <>
              <span>Verifying...</span>
            </>
          ) : (
            <>
              <span>🔒</span>
              <span>Login with Truecaller</span>
            </>
          )}
        </button>

        {error && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: '#fee',
            color: '#c00',
            borderRadius: '4px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <p style={{
          marginTop: '2rem',
          fontSize: '12px',
          color: '#999'
        }}>
          By continuing, you agree to share your verified phone number with Numdle
        </p>
      </div>
    </div>
  );
}
